import React, { useState } from 'react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { ChevronDown, ChevronUp, Trash2, Clock } from 'lucide-react';
import { cn } from '../lib/utils';

export default function AnswerHistory({ history, onClear }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getAnswerColor = (type) => {
    switch (type) {
      case 'positive':
        return 'text-emerald-400';
      case 'negative':
        return 'text-rose-400';
      default:
        return 'text-amber-400';
    }
  };

  const getAnswerBg = (type) => {
    switch (type) {
      case 'positive':
        return 'bg-emerald-500/10 border-emerald-500/20';
      case 'negative':
        return 'bg-rose-500/10 border-rose-500/20';
      default:
        return 'bg-amber-500/10 border-amber-500/20';
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="w-full max-w-lg mx-auto mt-8">
      {/* Toggle button */}
      <Button
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full h-12 justify-between px-6 rounded-xl",
          "bg-secondary/20 hover:bg-secondary/30",
          "border border-border/30",
          "transition-all duration-300"
        )}
      >
        <span className="flex items-center gap-2 text-muted-foreground">
          <Clock className="w-4 h-4" />
          Recent Questions ({history.length})
        </span>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
      </Button>

      {/* History panel */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-500 ease-in-out",
          isExpanded ? "max-h-[400px] opacity-100 mt-3" : "max-h-0 opacity-0"
        )}
      >
        <div className="glass rounded-xl p-4">
          {/* Clear button */}
          <div className="flex justify-end mb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              className="text-xs text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Clear History
            </Button>
          </div>

          {/* History list */}
          <ScrollArea className="h-[280px] pr-4">
            <div className="space-y-3">
              {history.map((item, index) => (
                <div
                  key={item.id}
                  className={cn(
                    "p-4 rounded-lg border transition-all duration-300",
                    "hover:scale-[1.01]",
                    getAnswerBg(item.type)
                  )}
                  style={{
                    animationDelay: `${index * 50}ms`,
                  }}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-sm text-foreground/90 font-medium leading-snug">
                      &ldquo;{item.question}&rdquo;
                    </p>
                    <span className="text-[10px] text-muted-foreground/60 whitespace-nowrap">
                      {formatTime(item.timestamp)}
                    </span>
                  </div>
                  <p className={cn("text-sm font-semibold", getAnswerColor(item.type))}>
                    → {item.answer}
                  </p>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
